import React, {useMemo, useEffect, useCallback, useState} from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import debounce from 'lodash/debounce'
import delay from 'lodash/delay'

import setTypingIndicator from 'modules/chat/utils/setTypingIndicator'
import {Input, useFormContext} from 'modules/common/components/Form'

import styles from './styles.module.scss'

export const FIELD_NAME = 'message'

const TYPING_DEBOUNCE = 500
const TYPING_RESET_DELAY = 10 * 1000

const InputField = ({roomId, user, disabled, innerRef}) => {
  const [lastTypingTime, setLastTypingTime] = useState(null)
  const {values, isValid, submitForm} = useFormContext()
  const message = get(values, FIELD_NAME)

  const setUserIsNotTyping = useCallback(
    () => setTypingIndicator({roomId, user, isTyping: false}),
    [roomId, user]
  )

  const setUserIsTyping = useMemo(
    () =>
      debounce(
        () => {
          setTypingIndicator({roomId, user, isTyping: true})
          setLastTypingTime(new Date().getTime())
        },
        TYPING_DEBOUNCE,
        {leading: true, trailing: true}
      ),
    [roomId, user]
  )

  useEffect(() => {
    if (message) {
      setUserIsTyping()
    }
  }, [message, setUserIsTyping])

  // @TODO:
  // we need some server side mechanism to reset the `isTyping` field
  // after a specified interval of time. otherwise the `isTyping` field
  // could be true forever if for some reason the client connection fails
  // or the client closes the browser window before the field could be reset
  useEffect(() => {
    if (!lastTypingTime) return

    const timeoutId = delay(setUserIsNotTyping, TYPING_RESET_DELAY)
    return () => clearTimeout(timeoutId)
  }, [lastTypingTime, setUserIsNotTyping])

  const handleKeyDown = useCallback(
    ev => {
      if (isValid && !ev.shiftKey && ev.keyCode === 13) {
        ev.preventDefault()
        submitForm()
      }
    },
    [isValid, submitForm]
  )

  return (
    <Input
      innerRef={innerRef}
      name={FIELD_NAME}
      type="textarea"
      fieldClassName={styles.inputField}
      controlClassName={styles.control}
      helpTextClassName="is-hidden-mobile is-size-7 has-text-right has-text-grey"
      helpText={
        <>
          Use &quot;:&quot; for Emojis 🚀 &#124; &#x21E7; + &#8629; for newline
          &#124; &#8629; to send
        </>
      }
      placeholder="Type your message!"
      minRows={1}
      maxRows={4}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      hideErrors
      hasEmojiAutoComplete
    />
  )
}

InputField.defaultProps = {
  disabled: false,
  roomId: null,
  user: null,
}

InputField.propTypes = {
  disabled: PropTypes.bool,
  innerRef: PropTypes.func,
  roomId: PropTypes.string,
  user: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
  }),
}

export default InputField
